import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { Alert } from "@offstreetparking/design-system/feedback";
import { CalendarXIcon } from "@offstreetparking/design-system/icons/CalendarXIcon";
import { ParkingIcon } from "@offstreetparking/design-system/icons/ParkingIcon";
import { TimeIcon } from "@offstreetparking/design-system/icons/TimeIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { DateTime } from "luxon";
import { OverrideNoticeAlert } from "../../../OverrideNoticeAlert/OverrideNoticeAlert";
import { Form } from "../../../forms/Form";
import type { ParkingChoiceFormData, WizardParkingChoice } from "../../PermitContextProvider/types";
import { makePermitIntervals } from "../../makePermitIntervals";
import type { OverrideNotices } from "../../types";
import { ParkingOptionsList } from "./ParkingOptionsList";
import { hasNeverExpiresIntervals } from "./hasNeverExpiresIntervals";
import { permitTimeSchema } from "./permitTimeSchema";

const FIELD_LABEL_MESSAGES = {
	selectedChoicesIds: msg`Parking Options`,
	locationIds: msg`Location`,
	permitIntervals: msg`Parking Time`,
} satisfies Record<string, MessageDescriptor>;

const makePermitTimeSchema = ({
	availableChoices,
}: {
	availableChoices: WizardParkingChoice[];
}) => {
	return permitTimeSchema.superRefine((val, ctx) => {
		const selectedChoices = val.choices.filter((x) => val.selectedChoicesIds.includes(x.parkingChoiceId));
		if (selectedChoices.length === 0) {
			ctx.addIssue({
				code: "too_small",
				origin: "string",
				inclusive: true,
				exact: false,
				minimum: 1,
				params: { label: FIELD_LABEL_MESSAGES.selectedChoicesIds },
				path: ["selectedChoicesIds"],
			});
		}
		for (const choice of selectedChoices) {
			const parkingChoice = availableChoices.find((pc) => pc.permitTypeId === choice.parkingChoiceId);
			if (parkingChoice?.locationChoiceEnabled && choice.locationIds.length === 0) {
				const index = val.choices.findIndex((x) => x.parkingChoiceId === choice.parkingChoiceId);
				ctx.addIssue({
					code: "too_small",
					origin: "string",
					inclusive: true,
					exact: false,
					minimum: 1,
					params: { label: FIELD_LABEL_MESSAGES.locationIds },
					path: ["choices", index, "locationIds"],
				});
			}
			const neverExpires = hasNeverExpiresIntervals(parkingChoice?.time);
			if (
				!choice.permitIntervals ||
				choice.permitIntervals.length === 0 ||
				!choice.permitIntervals.every(
					(interval) =>
						interval.startDate &&
						interval.startTime &&
						(neverExpires || (interval.endDate && interval.endTime)),
				)
			) {
				const index = val.choices.findIndex((x) => x.parkingChoiceId === choice.parkingChoiceId);
				ctx.addIssue({
					code: "too_small",
					origin: "string",
					inclusive: true,
					exact: false,
					minimum: 1,
					params: { label: FIELD_LABEL_MESSAGES.permitIntervals },
					path: ["choices", index, "permitIntervals"],
				});
			}
		}
	});
};
type ParkingTimeFormProps = {
	timezone: string;
	availableChoices: WizardParkingChoice[];
	upcomingChoices: WizardParkingChoice[];
	onSubmit: (data: ParkingChoiceFormData) => void;
	formId: string;
	initialValues?: ParkingChoiceFormData;
	preSelectedLocationId?: number;
	overrideNotices?: OverrideNotices;
	onSelectOverridePermitType?: (permitTypeId: number) => void;
};
export const ParkingTimeForm = ({
	timezone,
	availableChoices,
	upcomingChoices,
	onSubmit,
	formId,
	initialValues,
	preSelectedLocationId,
	overrideNotices,
	onSelectOverridePermitType,
}: ParkingTimeFormProps) => {
	const { t } = useLingui();
	const selectableInitialChoices = availableChoices.filter(
		(choice) => choice.override?.state !== "disabled" && choice.availability !== "outside-recurring-window",
	);
	const notices = overrideNotices ?? [];
	const canAutoSelectSingleChoice = notices.length === 0;
	return (
		<Form
			id={formId}
			initialValues={{
				selectedChoicesIds: initialValues?.parkingChoiceId
					? [initialValues.parkingChoiceId]
					: canAutoSelectSingleChoice && selectableInitialChoices.length === 1 && selectableInitialChoices[0]
						? [selectableInitialChoices[0].permitTypeId]
						: [],
				choices: [...availableChoices, ...upcomingChoices].map((pc) => {
					const isInitialChoice = initialValues?.parkingChoiceId === pc.permitTypeId;
					const permitIntervals =
						isInitialChoice && initialValues?.permitIntervals
							? initialValues.permitIntervals.map((interval) => ({
									startDate: interval.startDateTimeISO
										? DateTime.fromISO(interval.startDateTimeISO)
												.setZone(timezone)
												.toFormat("yyyy-MM-dd")
										: "",
									startTime: interval.startDateTimeISO
										? DateTime.fromISO(interval.startDateTimeISO)
												.setZone(timezone)
												.toFormat("HH:mm:ss")
										: "",
									endDate: interval.endDateTimeISO
										? DateTime.fromISO(interval.endDateTimeISO)
												.setZone(timezone)
												.toFormat("yyyy-MM-dd")
										: undefined,
									endTime: interval.endDateTimeISO
										? DateTime.fromISO(interval.endDateTimeISO)
												.setZone(timezone)
												.toFormat("HH:mm:ss")
										: undefined,
									schedules: interval.schedules,
								}))
							: makePermitIntervals(pc.time, timezone);
					const hasPreSelectedLocation =
						preSelectedLocationId &&
						pc.locationChoiceEnabled &&
						pc.locations.some((l) => l.id === preSelectedLocationId);
					return {
						parkingChoiceId: pc.permitTypeId,
						locationIds:
							isInitialChoice && initialValues?.locationIds && initialValues.locationIds.length > 0
								? initialValues.locationIds
								: hasPreSelectedLocation
									? [preSelectedLocationId]
									: pc.hasPinnedLocations
										? pc.locations.map((l) => l.id)
										: [],
						permitIntervals,
					};
				}),
			}}
			onSubmit={(val) => {
				const selectedChoice = val.choices.find((c) => val.selectedChoicesIds.includes(c.parkingChoiceId));
				onSubmit({
					parkingChoiceId: selectedChoice?.parkingChoiceId ?? 0,
					locationIds: selectedChoice?.locationIds ?? [],
					permitIntervals:
						selectedChoice?.permitIntervals.map((interval) => ({
							startDateTimeISO: DateTime.fromFormat(
								`${interval.startDate} ${interval.startTime}`,
								"yyyy-MM-dd HH:mm:ss",
								{
									zone: timezone,
								},
							)
								.toUTC()
								.toISO()!,
							endDateTimeISO:
								interval.endDate && interval.endTime
									? DateTime.fromFormat(
											`${interval.endDate} ${interval.endTime}`,
											"yyyy-MM-dd HH:mm:ss",
											{
												zone: timezone,
											},
										)
											.toUTC()
											.toISO()!
									: undefined,
							schedules: interval.schedules,
						})) ?? [],
				});
			}}
			schema={makePermitTimeSchema({
				availableChoices,
			})}
		>
			{({ setValue, getValues, formState: { errors } }) => {
				const isSingleOption = availableChoices.length === 1 && upcomingChoices.length === 0;
				const useSingleOptionLayout = isSingleOption && notices.length === 0;
				return (
					<div className="flex w-full flex-col gap-4">
						<div className="flex w-full max-w-prose flex-col gap-4">
							<Heading id="parking-options-heading" className="flex items-center gap-2">
								<AccessibleIcon>
									{useSingleOptionLayout ? (
										<ParkingIcon className="mt-0.5 h-5 min-h-5 w-5 min-w-5" />
									) : (
										<TimeIcon className="mt-0.5 h-5 min-h-5 w-5 min-w-5" />
									)}
								</AccessibleIcon>
								{useSingleOptionLayout ? t`Parking Details` : t`Parking Options`}
							</Heading>

							{errors.selectedChoicesIds?.message && (
								<div role="alert" aria-atomic="true">
									<Alert type="error" title={t`Select a parking option`} />
								</div>
							)}

							{availableChoices.length === 0 && upcomingChoices.length === 0 && (
								<OverrideNoticeAlert
									notices={overrideNotices}
									onSelectPermitType={onSelectOverridePermitType}
								/>
							)}

							{availableChoices.length === 0 && upcomingChoices.length === 0 ? (
								<div aria-live="polite" aria-atomic="true">
									{notices.length > 0 ? (
										<div className="flex flex-col items-center gap-3 py-8">
											<AccessibleIcon>
												<CalendarXIcon className="h-8 w-8 text-neutral-faded" />
											</AccessibleIcon>
											<Text weight="bold">{t`No Parking Options available`}</Text>
										</div>
									) : (
										<Alert type="info" title={t`Registration is currently unavailable`} />
									)}
								</div>
							) : (
								<ParkingOptionsList
									aria-labelledby="parking-options-heading"
									parkingOptions={availableChoices}
									overrideNotices={overrideNotices}
									onSelectOverridePermitType={onSelectOverridePermitType}
									timezone={timezone}
									preSelectedLocationId={preSelectedLocationId}
									isSingleOption={useSingleOptionLayout}
									onSelectOption={(optionId, locationId) => {
										const choiceIndex = getValues("choices").findIndex(
											(c) => c.parkingChoiceId === optionId,
										);
										const parkingChoice = availableChoices.find(
											(pc) => pc.permitTypeId === optionId,
										);
										const currentLocationIds = getValues(`choices.${choiceIndex}.locationIds`);
										const locationIds = locationId ? [locationId] : currentLocationIds;
										setValue("selectedChoicesIds", [optionId], {
											shouldValidate: true,
										});
										setValue(`choices.${choiceIndex}.locationIds`, locationIds, {
											shouldValidate: !parkingChoice?.locationChoiceEnabled,
										});
										if (parkingChoice) {
											const existingPermitIntervals = getValues(
												`choices.${choiceIndex}.permitIntervals`,
											);
											const intervals =
												existingPermitIntervals?.length > 0
													? existingPermitIntervals
													: makePermitIntervals(parkingChoice.time, timezone);
											if (intervals.length > 0) {
												setValue(`choices.${choiceIndex}.permitIntervals`, intervals);
											}
										}
									}}
									onSelectTime={(intervals) => {
										const selectedChoiceId = getValues("selectedChoicesIds")?.[0];
										if (!selectedChoiceId) {
											return;
										}
										const choiceIndex = getValues("choices").findIndex(
											(c) => c.parkingChoiceId === selectedChoiceId,
										);
										if (choiceIndex === -1) {
											return;
										}
										setValue(`choices.${choiceIndex}.permitIntervals`, intervals);
									}}
								/>
							)}
						</div>
						{upcomingChoices.length > 0 && (
							<div className="flex w-full max-w-prose flex-col gap-4">
								<Heading>{t`Opening soon`}</Heading>
								<ParkingOptionsList
									aria-label={t`Opening soon`}
									parkingOptions={upcomingChoices}
									timezone={timezone}
									onSelectOption={() => {}}
									onSelectTime={(_intervals) => {}}
								/>
							</div>
						)}
					</div>
				);
			}}
		</Form>
	);
};
